import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import sampleSize from 'lodash/sampleSize';
import pullAt from 'lodash/pullAt';
import get from 'lodash/get';

export const set = (property) => (state, payload) => (state[property] = payload);
export const toggle = (property) => (state) => (state[property] = !state[property]);

export const isNumericValue = (value) => !isNaN(parseFloat(value));

export const isInternetExplorer11 = () => Boolean(window.MSInputMethodContext && document.documentMode);

export const waitForRef = async (ctx, refName, maxWaitMs = 2000, intervalMs = 250) => {
  const start = Date.now();
  while (Date.now() - start < maxWaitMs) {
    await ctx.$nextTick();
    const ref = get(ctx, refName);
    if (ref) {
      return ref;
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  throw new Error(`Ref "${refName}" not available after ${maxWaitMs}ms`);
};

/// make sure to have a unique tree, with no element being contained in another item of the tree-list
export const mergeTrees = function (treeList) {
  const isContained = (item, treeListParam) => {
    if (treeListParam.findIndex((listItem) => listItem.id === item.id) >= 0) {
      return true;
    }
    for (let i = 0; i < treeListParam.length; i++) {
      const listItem = treeListParam[i];
      if ((listItem.children || []).length > 0) {
        if (isContained(item, listItem.children)) {
          return true;
        }
      }
    }
    return false;
  };

  let mergedTreeList = [];
  for (let i = 0; i < treeList.length; i++) {
    const listItem = treeList[i];
    let otherTreeListItems = cloneDeep(treeList);
    pullAt(otherTreeListItems, [i]);
    if (!isContained(listItem, otherTreeListItems)) {
      mergedTreeList.push(listItem);
    }
  }
  return mergedTreeList;
};

export const buildTreeFromList = function (parentId, elementList) {
  const currentLevel =
    parentId === null
      ? // find all the root-elements that do not have a parent (in the list)
        elementList.filter((element) => {
          return !elementList.find((parentElement) => parentElement.id === element.parentId);
        })
      : // otherwise look for the parent directly
        elementList.filter((element) => element.parentId === parentId);

  let branches = [];
  currentLevel.forEach((element) => {
    let branch = cloneDeep(element);
    branch.children = buildTreeFromList(branch.id, elementList).sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
    branches.push(branch);
  });
  branches = branches.sort((a, b) => {
    return a.name.localeCompare(b.name);
  });

  return branches;
};

export const fixedDecimals = (number, decimalPoints = 0) => {
  const num = parseFloat(number);
  decimalPoints = Math.floor(decimalPoints);

  if (isNaN(num) || num === Infinity) {
    return null;
  }

  if (decimalPoints <= 0) {
    return parseInt(num, 10).toString();
  }

  const decimalPointsParam = isNumericValue(decimalPoints) ? decimalPoints : 0;
  const multiplier = 10 ** decimalPointsParam;
  const fixedString = (Math.round((num + Number.EPSILON) * multiplier) / multiplier)
    .toFixed(decimalPointsParam)
    .toString();

  return fixedString;
};

export const padStart = (string, len = 5, pad = '0') => {
  const str = String(string);

  if (pad.length > 1) {
    pad = pad[0];
  }

  return Array.from({ length: len - str.length }, () => pad).join('') + str;
};

export const leftPadNumber = (number, len = 5, pad = '0') => {
  const matches = String(number).match(/^(-)?(\d+)(\.\d+)?/);

  if (!matches) {
    return null;
  }

  const sign = matches[1] || '';
  const int = matches[2];
  const frac = matches[3] || '';
  const lenWithoutInt = sign.length + frac.length;

  return [sign, padStart(int, len - lenWithoutInt, pad), frac].join('');
};

export const randomInt = function (max) {
  return Math.floor(Math.random() * max) + 1;
};

export const isNullOrEmpty = function (string) {
  return string === null || string === undefined || (typeof string === 'string' && string.trim() === '');
};

export const kebabCaseToCamelCase = (str) => {
  return str
    .replace(/^-/, '')
    .replace(/-$/, '')
    .split('-')
    .map((part, idx) => {
      return idx === 0 ? part.toLowerCase() : part.slice(0, 1).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join('');
};

export const readableFileSizeFromBytes = function (bytes) {
  if (typeof bytes !== 'number' || isNaN(bytes)) {
    return '';
  }

  let exponent;
  let unit;
  const neg = bytes < 0;
  const units = ['B', 'kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  if (neg) {
    bytes = -bytes;
  }

  if (bytes < 1) {
    return '0 B';
  }

  exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1000)), units.length - 1);
  bytes = (bytes / Math.pow(1000, exponent)).toFixed(2);
  unit = units[exponent];

  return `${(neg ? '-' : '') + bytes} ${unit}`;
};

export const gatewayMgmtMixin = {
  methods: {
    clearGMInterval(intID) {
      clearInterval(intID);
    },
    convertMinutesToSeconds(minutes) {
      return Math.floor(minutes * 60);
    },
  },
};

export const checkImageUrl = function (url, callback, timeout = 5000) {
  let timedOut = false,
    timer;
  let img = new Image();

  img.onerror = img.onabort = function () {
    if (!timedOut) {
      clearTimeout(timer);
      callback(url, 'error');
    }
  };
  img.onload = function () {
    if (!timedOut) {
      clearTimeout(timer);
      callback(url, 'success');
    }
  };
  img.src = url;
  timer = setTimeout(function () {
    timedOut = true;
    // reset .src to invalid URL so it stops previous loading, but doesn't trigger new load
    img.src = '//!!!!/test.jpg';
    callback(url, 'timeout');
  }, timeout);
};

export const validImageFileName = function (fileName) {
  return /\.(jpg|jpeg|png|gif|tiff)$/i.exec(fileName) !== null;
};

const breakPoints = [
  { width: 600, id: 'xs' },
  { width: 960, id: 'sm' },
  { width: 1264, id: 'md' },
  { width: 1904, id: 'lg' },
  { width: 9001, id: 'xl' },
  { width: 10000000, id: 'xxl' },
];
// for the mixin to work, it requires a template-ref element named 'container'!
export const relativeBreakpoint = {
  data() {
    return {
      relativeBreakpoint: {
        xs: null,
        sm: null,
        md: null,
        lg: null,
        xl: null,

        // Conditionals
        xsOnly: null,
        smOnly: null,
        smAndDown: null,
        smAndUp: null,
        mdOnly: null,
        mdAndDown: null,
        mdAndUp: null,
        lgOnly: null,
        lgAndDown: null,
        lgAndUp: null,
        xlOnly: null,

        name: null,
        width: null,
      },
    };
  },
  created() {
    window.addEventListener('resize', this.debouncedCalculation);
    this.debouncedCalculation();
  },
  destroyed() {
    window.removeEventListener('resize', this.debouncedCalculation);
  },
  methods: {
    debouncedCalculation: debounce(function () {
      const currentWidth = get(this.$refs, 'container.clientWidth');

      if (!currentWidth) {
        return;
      }

      this.relativeBreakpoint.width = currentWidth;

      for (let i = 0; i < breakPoints.length; i++) {
        const lastBp = i === 0 ? { width: 0, id: '' } : breakPoints[i - 1];
        const bp = breakPoints[i];

        const isActive = lastBp.width < currentWidth;
        const isExclusive = isActive && currentWidth < bp.width;

        this.relativeBreakpoint[`${bp.id}`] = isActive;
        this.relativeBreakpoint[`${bp.id}Only`] = isExclusive;
        this.relativeBreakpoint[`${bp.id}AndUp`] = isActive;
        this.relativeBreakpoint[`${bp.id}AndDown`] = isActive;

        if (isExclusive) {
          this.relativeBreakpoint.name = bp.id;
        }
      }
    }, 250),
  },
};

export const downloadAttachment = function (mimeType, data, fileName) {
  const blobData = [data];
  const blob = new Blob(blobData, { type: mimeType });
  if (typeof window.navigator.msSaveBlob !== 'undefined') {
    // IE workaround for "HTML7007: One or more blob URLs were
    // revoked by closing the blob for which they were created.
    // These URLs will no longer resolve as the data backing
    // the URL has been freed."
    window.navigator.msSaveBlob(blob, fileName);
  } else {
    const blobURL =
      window.URL && window.URL.createObjectURL
        ? window.URL.createObjectURL(blob)
        : window.webkitURL.createObjectURL(blob);
    const tempLink = document.createElement('a');
    tempLink.style.display = 'none';
    tempLink.href = blobURL;
    tempLink.setAttribute('download', fileName);

    // Safari thinks _blank anchor are pop ups. We only want to set _blank
    // target if the browser does not support the HTML5 download attribute.
    // This allows you to download files in desktop safari if pop up blocking
    // is enabled.
    if (typeof tempLink.download === 'undefined') {
      tempLink.setAttribute('target', '_blank');
    }

    document.body.appendChild(tempLink);
    tempLink.click();

    // Fixes "webkit blob resource error 1"
    setTimeout(function () {
      document.body.removeChild(tempLink);
      window.URL.revokeObjectURL(blobURL);
    }, 0);
  }
};

export const randomPassword = function (length = 15) {
  let chars = 'abcdefghkmnpqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~|}{[]:;?><-=';
  return sampleSize(chars, length).join('');
};

export const userItemsPerListMixin = {
  watch: {
    'tableOptions.itemsPerPage': function (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.setUserItemsPerList(newValue);
      }
    },
  },
  created() {
    if (this.tableOptions) {
      this.tableOptions.itemsPerPage = this.savedUserItemsPerList();
    }
  },
  methods: {
    savedUserItemsPerList() {
      return parseInt(this.$store.state.app.rowsPerPage) || 20;
    },
    setUserItemsPerList(itemsPerPage) {
      this.$store.commit('app/setRowsPerPage', itemsPerPage);
    },
  },
};

export const isCopyToClipboardSupported = () => {
  if (navigator && navigator.clipboard) {
    return true;
  }
  try {
    return document.queryCommandSupported('copy');
  } catch (error) {
    return false;
  }
};

export const copyToClipboard = (copyValue) => {
  if (navigator && navigator.clipboard) {
    navigator.clipboard.writeText(copyValue);
  } else {
    try {
      // create textarea to copy from
      const copyElement = document.createElement('textarea');
      copyElement.value = copyValue;
      // set styles to hide textarea from viewport
      copyElement.style.position = 'absolute';
      copyElement.style.top = '-99999px';
      // add to element to body otherwise we can't select/copy
      document.body.appendChild(copyElement);
      copyElement.select();
      document.execCommand('copy'); // copy text
      copyElement.remove(); // remove element
      // eslint-disable-next-line no-empty
    } catch (error) {
      console.error('Error while copy', error);
    }
  }
};

// replaces ä ü ö ß with ae ue oe ss, only for DE
export const replaceSpecialChars = (string) => {
  return (
    string
      .toLowerCase()
      // replace umlauts and ß
      .replace(/ä/g, 'ae')
      .replace(/ü/g, 'ue')
      .replace(/ö/g, 'oe')
      .replace(/ß/g, 'ss')
      // normalize diacritics
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[ ]+/g, '_')
      // any special characters now are thrown out
      .replace(/[^a-z0-9_\-.]+/g, '')
  );
};
